import { useEffect, useState } from "react";
import { getAdminHeaders } from "./adminApi";

const emptyForm = {
  title: "",
  slug: "",
  summary: "",
  content: "",
  category_id: "",
  seo_title: "",
  seo_description: "",
  related_slugs: "",
};

export default function AdminGrowth() {
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [status, setStatus] = useState("draft");
  const [message, setMessage] = useState("");
  const [checks, setChecks] = useState({
    reviewed: false,
    distinct_from_existing: false,
    no_private_or_copyright: false,
  });

  const load = async () => {
    const [categoryRes, articleRes] = await Promise.all([
      fetch("/api/admin/growth/categories", { headers: getAdminHeaders() }),
      fetch("/api/admin/growth/articles", { headers: getAdminHeaders() }),
    ]);
    const categoryData = await categoryRes.json();
    const articleData = await articleRes.json();
    if (categoryRes.ok) setCategories(categoryData.items || []);
    if (articleRes.ok) setItems(articleData.items || []);
  };

  useEffect(() => {
    load();
  }, []);

  const onChange = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const editItem = (item) => {
    setEditingId(item.id);
    setStatus(item.status);
    setForm({
      title: item.title || "",
      slug: item.slug || "",
      summary: item.summary || "",
      content: item.content || "",
      category_id: item.category_id || "",
      seo_title: item.seo_title || "",
      seo_description: item.seo_description || "",
      related_slugs: item.related_slugs || "",
    });
    setMessage("");
  };

  const save = async () => {
    const payload = {
      ...form,
      category_id: Number(form.category_id),
    };
    const res = await fetch(editingId ? `/api/admin/growth/articles/${editingId}` : "/api/admin/growth/articles", {
      method: editingId ? "PATCH" : "POST",
      headers: getAdminHeaders(true),
      body: JSON.stringify(payload),
    });
    const data = await res.json();
    if (!res.ok) {
      throw new Error(data.detail || "保存失败");
    }
    setEditingId(data.item.id);
    setStatus(data.item.status);
    return data.item;
  };

  const saveDraft = async (event) => {
    event.preventDefault();
    try {
      await save();
      setMessage("草稿已保存。草稿不会出现在公开页面。");
      load();
    } catch (err) {
      setMessage(err.message);
    }
  };

  const publish = async () => {
    if (!window.confirm("确认这篇文章已经人工审核，可以公开？")) return;
    try {
      const item = await save();
      const res = await fetch(`/api/admin/growth/articles/${item.id}/publish`, {
        method: "POST",
        headers: getAdminHeaders(true),
        body: JSON.stringify(checks),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || "发布失败");
      }
      setStatus("published");
      setMessage(`已公开：${window.location.origin}/growth/${data.item.slug}`);
      load();
    } catch (err) {
      setMessage(err.message);
    }
  };

  const unpublish = async () => {
    if (!editingId) return;
    if (!window.confirm("下架后，匿名访问会返回 404。")) return;
    const res = await fetch(`/api/admin/growth/articles/${editingId}/unpublish`, {
      method: "POST",
      headers: getAdminHeaders(),
    });
    const data = await res.json();
    if (!res.ok) {
      setMessage(data.detail || "下架失败");
      return;
    }
    setStatus("archived");
    setMessage("已下架");
    load();
  };

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold">关系成长文章</h2>
        <p className="text-sm text-gray-500 mt-1">先保存草稿，预览确认后再发布。发布不会自动写入未审核内容。</p>
      </div>

      <form onSubmit={saveDraft} className="bg-white rounded-2xl p-6 space-y-4 shadow">
        <div className="grid md:grid-cols-2 gap-4">
          <label className="block text-sm">
            标题
            <input className="mt-1 w-full border rounded-xl px-3 py-2" value={form.title} onChange={(e) => onChange("title", e.target.value)} required />
          </label>
          <label className="block text-sm">
            公开地址
            <input className="mt-1 w-full border rounded-xl px-3 py-2" value={form.slug} onChange={(e) => onChange("slug", e.target.value)} placeholder="listening-in-relationship" required />
          </label>
        </div>
        <label className="block text-sm">
          栏目
          <select className="mt-1 w-full border rounded-xl px-3 py-2" value={form.category_id} onChange={(e) => onChange("category_id", e.target.value)} required>
            <option value="">选择栏目</option>
            {categories.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </select>
        </label>
        <label className="block text-sm">
          摘要
          <textarea className="mt-1 w-full border rounded-xl px-3 py-2 h-20" value={form.summary} onChange={(e) => onChange("summary", e.target.value)} />
        </label>
        <label className="block text-sm">
          正文
          <textarea className="mt-1 w-full border rounded-xl px-3 py-2 h-64" value={form.content} onChange={(e) => onChange("content", e.target.value)} />
        </label>
        <div className="grid md:grid-cols-2 gap-4">
          <label className="block text-sm">
            SEO 标题
            <input className="mt-1 w-full border rounded-xl px-3 py-2" value={form.seo_title} onChange={(e) => onChange("seo_title", e.target.value)} />
          </label>
          <label className="block text-sm">
            SEO 描述
            <input className="mt-1 w-full border rounded-xl px-3 py-2" value={form.seo_description} onChange={(e) => onChange("seo_description", e.target.value)} />
          </label>
        </div>
        <fieldset className="text-sm space-y-2">
          <legend className="font-medium">相关文章</legend>
          {items.filter((item) => item.slug !== form.slug).map((item) => {
            const selected = (form.related_slugs || "").split(",").filter(Boolean);
            return (
              <label key={item.slug} className="flex gap-2 items-center">
                <input
                  type="checkbox"
                  checked={selected.includes(item.slug)}
                  onChange={(e) => {
                    const next = e.target.checked
                      ? [...selected, item.slug].slice(0, 3)
                      : selected.filter((slug) => slug !== item.slug);
                    onChange("related_slugs", next.join(","));
                  }}
                />
                {item.title}
              </label>
            );
          })}
        </fieldset>
        <div className="rounded-xl bg-gray-50 p-4 text-sm text-gray-600 space-y-2">
          <p>发布前必须逐项确认。未勾选时，后台会拒绝公开。</p>
          <label className="flex gap-2"><input type="checkbox" checked={checks.reviewed} onChange={(e) => setChecks((prev) => ({ ...prev, reviewed: e.target.checked }))} />已经人工审核</label>
          <label className="flex gap-2"><input type="checkbox" checked={checks.distinct_from_existing} onChange={(e) => setChecks((prev) => ({ ...prev, distinct_from_existing: e.target.checked }))} />和已有文章有明确区别</label>
          <label className="flex gap-2"><input type="checkbox" checked={checks.no_private_or_copyright} onChange={(e) => setChecks((prev) => ({ ...prev, no_private_or_copyright: e.target.checked }))} />没有会员隐私、私人咨询和未授权书摘</label>
          <p>当前状态：{status === "published" ? "已公开" : status === "archived" ? "已下架" : "草稿"}</p>
          {status === "published" && form.slug ? <p>公开 URL：/growth/{form.slug}</p> : <p>草稿没有公开 URL。</p>}
        </div>
        {form.content ? (
          <div className="border rounded-xl p-4 space-y-3">
            <h3 className="font-bold">预览</h3>
            <h4 className="text-xl">{form.title}</h4>
            <p>{form.summary}</p>
            {form.content.split(/\n\s*\n/).filter((block) => block.trim()).map((block) => (
              <p key={block.slice(0, 30)} className="whitespace-pre-line">{block}</p>
            ))}
          </div>
        ) : null}
        <div className="flex flex-wrap gap-3">
          <button type="submit" className="px-4 py-2 rounded-xl bg-gray-800 text-white">保存草稿</button>
          <button type="button" onClick={publish} disabled={!(checks.reviewed && checks.distinct_from_existing && checks.no_private_or_copyright)} className="px-4 py-2 rounded-xl bg-rose-600 text-white disabled:bg-gray-300">发布</button>
          {status === "published" ? (
            <button type="button" onClick={unpublish} className="px-4 py-2 rounded-xl border">下架</button>
          ) : null}
          <button
            type="button"
            onClick={() => {
              setEditingId(null);
              setStatus("draft");
              setForm(emptyForm);
              setMessage("");
            }}
            className="px-4 py-2 rounded-xl border"
          >
            新建
          </button>
        </div>
        {message ? <p className="text-sm text-rose-700">{message}</p> : null}
      </form>

      <div className="bg-white rounded-2xl shadow divide-y">
        {items.length === 0 ? <p className="p-6 text-gray-400">还没有文章</p> : null}
        {items.map((item) => (
          <button key={item.id} type="button" onClick={() => editItem(item)} className="w-full text-left p-4 hover:bg-rose-50">
            <span className="font-medium">{item.title}</span>
            <span className="ml-3 text-sm text-gray-400">{item.status === "published" ? "已公开" : item.status === "archived" ? "已下架" : "草稿"}</span>
            <span className="ml-3 text-sm text-gray-400">/growth/{item.slug}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
